import { PropType, computed, defineComponent, ref, watch } from 'vue'
import { Store } from 'pinia'
import { PowerResource } from '../iconRender/icon.model'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { RouteCDNModel } from '../../models/detail.model'
import { calcSize, drawPointCountry, resizeMapResources } from '../chargerMap/mapUtils'
import { DEFAULT_CITY_ZOOM_LEVEL, DEFAULT_COUNTRY_ZOOM_LEVEL } from '../../config/zoomLevelConfig'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { Line, Point, PowerRoute } from '../../contract/map'
import { highwayRouteColor } from '../recommendRender/utils'
import { getChargingTripRouteCDN } from '../recommendRender/recommend.api'

/**
 * 信发设备-全国小点路线渲染功能模块
 */
export default defineComponent({
  name: 'xinfa-dot-line-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
    cdnUrl: {
      type: RouteCDNModel,
      default: null,
    },
  },

  setup: (props) => {
    const mapContextRef = computed(() => props.mapContextRef)
    // const mapInstance = computed(() => props.mapInstance)
    const zoomLevel = ref<number>(0)

    const allResources: any = {
      swap: [] as PowerResource[],
      charge: [] as PowerResource[],
      third: [] as PowerResource[],
      nioStore: [] as PowerResource[],
      testDrive: [] as PowerResource[],
    }

    let recommendRoutes: PowerRoute[] = [] // 推荐路线
    let highwayTag = '' // 区分高速的标签名

    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig

    /**
     * 通过cdn获取资源点
     * @param key
     * @param cdnUrlRes
     * @param display 加载完成后是否展示
     * @returns
     */
    const getResourcesByCND = (key: string, cdnUrlRes: any, display = true) => {
      allResources[key] = [] as PowerResource[]
      if (key == 'third' && !componentsConfig?.third_resource?.show) {
        // 获取是否支持全国三方显示
        return
      }
      const linkKey = key + 'ResourcesLink'
      if (cdnUrlRes[linkKey]?.length) {
        let index = 0
        return Promise.all(
          cdnUrlRes[linkKey].map((url: string) =>
            window
              .fetch(url)
              .then((response) => response.json())
              .then((json) => {
                const res = json.map((item: any) => {
                  if (item?.id) {
                    const position = item?.location.split(',')
                    const point = gcj02ToBd09({
                      lng: Number(position[0]),
                      lat: Number(position[1]),
                    })
                    return {
                      id: item?.id,
                      lng: point?.lng,
                      lat: point?.lat,
                      type: key == 'swap' ? 'PowerSwap' : item.type,
                    } as Point
                  } else {
                    const position = item?.split(',')
                    return gcj02ToBd09({
                      lng: Number(position[0]),
                      lat: Number(position[1]),
                    })
                  }
                })
                index++
                drawPointCountry(props.mapContextRef!, res as Point[], key, key + index, display)
                allResources[key].push(...(res as Point[]))
                props.componentStore.$patch({
                  allResources: allResources,
                })
                return res
              })
          )
        )
      }
    }

    /**
     * 初始绘制推荐路线
     */
    const drawRecommendRoutePolyline = () => {
      if (!highwayTag) {
        highwayTag = props.componentStore.dictionaries?.filter((item) => {
          return item.key === 'h5_recommend_route_high_speed_config'
        })[0]?.value
      }
      drawRecommendRoute(props.mapContextRef!, recommendRoutes, highwayTag)
    }

    /**
     * 加载线路数据
     * @param cdnUrl
     */
    const getRecommendRoute = async (cdnUrlRes: any) => {
      const tripRoutes = await getChargingTripRouteCDN(cdnUrlRes.chargingTripRouteLink)
      const routes: PowerRoute[] = []
      if (tripRoutes && tripRoutes.length) {
        for (const tripRoute of tripRoutes) {
          const mapData = tripRoute.mapData
          const polyline = mapData.polyline
          const polylineRgeo = mapData.polylineRgeo // 起点终点的名称，用于画起点终点
          const route: PowerRoute = {
            id: tripRoute.id,
            description: tripRoute?.detail?.description,
            polyline: polyline.map((location: any) => {
              const position = location.split(',')
              return { lng: Number(position[0]), lat: Number(position[1]) }
            }),
            locations:
              polylineRgeo && polylineRgeo.length
                ? [polylineRgeo[0], polylineRgeo[polylineRgeo.length - 1]]
                : [], // 画起点终点的城市名称
            tags: tripRoute?.detail?.tags,
            resources: mapData?.resources || [],
            detail: tripRoute,
            distance: mapData.distance,
          }
          routes.push(route)
        }
      }
      recommendRoutes = routes
      props.componentStore.$patch({ recommendRoutes })
    }

    /**
     * 推荐路线全国绘制
     * @param {MapContext} context 地图上下文
     * @param {PowerRoute[]} recommendRoutes 推荐路线数据
     * @param {string} highwayTag 区分高速的标签名
     * @param {number} ratio 不同zoom级别缩放比例
     */
    const drawRecommendRoute = async (
      context: ChargeMapContext,
      recommendRoutes: PowerRoute[],
      highwayTag?: string,
      ratio = 0.8
    ) => {
      if (recommendRoutes && recommendRoutes.length) {
        const drawPolyline: Line[] = []
        recommendRoutes.forEach((item: PowerRoute) => {
          const color = highwayRouteColor(item.tags, highwayTag)
          drawPolyline.push({
            points: item.polyline.map((pi: Point) => {
              return gcj02ToBd09({
                lng: Number(pi.lng),
                lat: Number(pi.lat),
              })
            }),
            options: {
              strokeWeight: 1 * ratio,
              strokeColor: color,
            },
          } as Line)
        })
        // 画线
        context?.addPolylineMapvgl('highway', drawPolyline) // 使用mapvgl方式
      }
    }

    /**
     * 隐藏所有资源点
     */
    const hideAllPointLayer = () => {
      props.mapContextRef?.operatePointLayer('swap', false)
      props.mapContextRef?.operatePointLayer('charge', false)
      props.mapContextRef?.operatePointLayer('third', false)
      props.mapContextRef?.operatePointLayer('nioStore', false)
      props.mapContextRef?.operatePointLayer('testDrive', false)
      // props.mapContextRef?.operatePolylineLayer('highway', false)
    }

    /**
     * 隐藏所有资源点
     */
    const showAllPointLayer = () => {
      props.mapContextRef?.operatePointLayer('swap', true)
      props.mapContextRef?.operatePointLayer('charge', true)
      props.mapContextRef?.operatePointLayer('third', true)
      props.mapContextRef?.operatePointLayer('nioStore', true)
      props.mapContextRef?.operatePointLayer('testDrive', true)
      // props.mapContextRef?.operatePolylineLayer('highway', true)
    }

    // 优先让地图加载和渲染完成后再请求数据
    const mapReadyRequestData = async (cdnUrlRes: any) => {
      getResourcesByCND('swap', cdnUrlRes) // 获取全国换电站资源
      getResourcesByCND('charge', cdnUrlRes) // 获取全国充电桩资源
      getResourcesByCND('third', cdnUrlRes) // 获取全国三方资源
      getResourcesByCND('nioStore', cdnUrlRes) // 获取蔚来门店资源
      getResourcesByCND('testDrive', cdnUrlRes) // 获取试驾点资源
      await getRecommendRoute(cdnUrlRes) // 获取全国换电站资源
      drawRecommendRoutePolyline()
    }

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = props.mapInstance?.getZoom()
      // 20250605 因为大屏小点消失层级变深，所以信发上不需要处理大点
      resizeMapResources(mapContextRef.value!, calcSize(zoomLevel.value).w) // 调整点的大小
      // showAllPointLayer()
      if (zoomLevel.value <= DEFAULT_COUNTRY_ZOOM_LEVEL) {
        props.mapContextRef?.operatePointLayer('swap', true)
        props.mapContextRef?.operatePointLayer('charge', true)
        props.mapContextRef?.operatePointLayer('third', true)
        props.mapContextRef?.operatePointLayer('nioStore', true)
        props.mapContextRef?.operatePointLayer('testDrive', true)
        mapContextRef.value?.operatePolylineLayer('highway', true)
      } else {
        props.mapContextRef?.operatePointLayer('swap', true)
        props.mapContextRef?.operatePointLayer('charge', true)
        props.mapContextRef?.operatePointLayer('third', false)
        props.mapContextRef?.operatePointLayer('nioStore', true)
        props.mapContextRef?.operatePointLayer('testDrive', true)
      }
    }

    // 启动相关事件监听
    const initEventListener = () => {
      props.mapInstance?.addEventListener('zoomend', zoomEnd, false)
    }

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = async () => {
      initEventListener()
      mapReadyRequestData(props.cdnUrl)
    }
    watch(
      () => props.cdnUrl,
      () => {
        if (props.mapContextRef && props.cdnUrl) {
          onReady()
        }
      },
      {
        immediate: true,
      }
    )
    return () => {}
  },
})
